// Queue alerts, rebuilt from saved queue readings by replaying the alert rule the backend uses:
//   recovered + two readings in a row at the threshold or more (or overflowing) -> breach alert
//   breached  + two readings in a row under the threshold                      -> recovery alert
// One alert stream per stand section, as the backend runs it per (location, section).
// Response time: from the breach alert to the recovery alert.
// Blips: alerts that recovered within about a minute (the fastest the rule allows with 30-second
// readings). Too fast to be a staff response, so they count as alerts sent but are left out of
// every response-time number.
import moment from 'moment';
import { QUEUE_TIME_GOOD_MAX } from '../Concession/concessionTheme';
import { toVenueTime } from '../Concession/live/liveMetrics';

export const ALERT_AT = QUEUE_TIME_GOOD_MAX;

const MINUTE = 60 * 1000;
// Recovered within this many minutes (shows as "1m" or "<1m") = a blip
export const BLIP_MAX_MINUTES = 1.5;
export const isBlip = (alert) => alert.minutes != null && alert.minutes < BLIP_MAX_MINUTES;

// Readings in time order. A 0 queue isn't a reading (the backend's minimum is 1), so it's skipped
// unless it's flagged overflow.
export const sectionReadings = (points, timeZone) => (points || [])
  .map((p) => {
    const time = toVenueTime(p.time, timeZone);
    const v = Number(p.queueTime);
    return { time, t: new Date(time).getTime(), v: Number.isNaN(v) ? 0 : v, o: !!p.overflow };
  })
  .filter((r) => !Number.isNaN(r.t) && (r.v > 0 || r.o))
  .sort((a, b) => a.t - b.t);

const isHigh = (r) => r.o || r.v >= ALERT_AT;

// Replays the rule over one section's readings. Each alert: { start, end, minutes, peak, peakOverflow }.
// `end` (and `minutes`) is null when the queue hadn't recovered by the last reading.
export const replayAlerts = (readings) => {
  const alerts = [];
  let breached = false;
  let side = null;
  let streak = 0;
  let open = null;
  readings.forEach((r) => {
    const high = isHigh(r);
    if (high === side) streak += 1;
    else {
      side = high;
      streak = 1;
    }
    if (!breached && high && streak >= 2) {
      breached = true;
      open = { start: r.t, end: null, minutes: null, peak: r.v, peakOverflow: r.o };
      alerts.push(open);
    } else if (breached && !high && streak >= 2) {
      breached = false;
      open.end = r.t;
      open.minutes = (r.t - open.start) / MINUTE;
      open = null;
    }
    if (open && (r.v > open.peak || (r.v === open.peak && r.o))) {
      open.peak = r.v;
      open.peakOverflow = open.peakOverflow || r.o;
    }
  });
  return alerts;
};

const mean = (values) => (values.length ? values.reduce((s, v) => s + v, 0) / values.length : null);
const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Numbers for a list of alerts (one game, or the whole range). `count` is every alert sent, blips
// included; the response numbers (recovered, avg, median, longest, within10) leave blips out.
export const summarizeAlerts = (alerts) => {
  const blips = alerts.filter(isBlip).length;
  const recovered = alerts.filter((a) => a.minutes != null && !isBlip(a)).map((a) => a.minutes);
  return {
    count: alerts.length,
    blips,
    real: alerts.length - blips,
    recovered: recovered.length,
    open: alerts.filter((a) => a.minutes == null).length,
    avg: mean(recovered),
    median: median(recovered),
    longest: recovered.length ? Math.max(...recovered) : null,
    // Back under the target within this many minutes of the alert
    within10: recovered.filter((m) => m <= 10).length,
  };
};

// One game: every alert at the chosen stands, each tagged with its stand and section.
// days: { [locationId]: { sections: { [sectionId]: points } } } for that date
export const gameAlerts = (stands, days) => {
  const alerts = [];
  let hasData = false;
  stands.forEach((stand) => {
    const sections = days[stand.id]?.sections || {};
    (stand.sections || []).forEach((s) => {
      const readings = sectionReadings(sections[String(s.sectionId)], stand.timeZone);
      if (readings.length) hasData = true;
      replayAlerts(readings).forEach((a) => alerts.push({ ...a, standId: stand.id, standName: stand.name, sectionId: s.sectionId }));
    });
  });
  return { alerts: alerts.sort((a, b) => a.start - b.start), hasData };
};

// Response time as "6m", "1h 5m"; under a minute as "<1m"
export const formatResponse = (minutes) => {
  if (minutes == null) return '—';
  if (minutes < 1) return '<1m';
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
};

export const alertClock = (t) => moment(t).format('h:mm A');
